// Client for the French government company-registry search API.
// recherche-entreprises.api.gouv.fr — public, free, no API key required, CORS-enabled.
// Docs: https://recherche-entreprises.api.gouv.fr/docs/

const SEARCH_URL = 'https://recherche-entreprises.api.gouv.fr/search';
const PER_PAGE = 10;

export interface CompanySearchResult {
	/** SIREN — 9-digit company identifier (stable, unique per company) */
	siren: string;
	/** Legal / display name */
	name: string;
	/** Head-office postal code, when known */
	postalCode?: string;
	/** Head-office city, when known */
	city?: string;
	/** Human-readable activity sector (NAF section), falling back to the raw NAF code */
	sector?: string;
}

interface RawSiege {
	code_postal?: string | null;
	libelle_commune?: string | null;
	activite_principale?: string | null;
}

interface RawResult {
	siren: string;
	nom_complet?: string | null;
	nom_raison_sociale?: string | null;
	section_activite_principale?: string | null;
	siege?: RawSiege | null;
}

interface RawResponse {
	results?: RawResult[];
}

// NAF rév. 2 sections — turns the single-letter activity code into a readable
// "secteur d'activité". Falls back to the raw NAF code when the letter is unknown.
const NAF_SECTIONS: Record<string, string> = {
	A: 'Agriculture, sylviculture et pêche',
	B: 'Industries extractives',
	C: 'Industrie manufacturière',
	D: "Production et distribution d'électricité et de gaz",
	E: "Production et distribution d'eau, assainissement, déchets",
	F: 'Construction',
	G: "Commerce, réparation d'automobiles et de motocycles",
	H: 'Transports et entreposage',
	I: 'Hébergement et restauration',
	J: 'Information et communication',
	K: "Activités financières et d'assurance",
	L: 'Activités immobilières',
	M: 'Activités spécialisées, scientifiques et techniques',
	N: 'Activités de services administratifs et de soutien',
	O: 'Administration publique',
	P: 'Enseignement',
	Q: 'Santé humaine et action sociale',
	R: 'Arts, spectacles et activités récréatives',
	S: 'Autres activités de services',
	T: "Activités des ménages en tant qu'employeurs",
	U: 'Activités extra-territoriales',
};

function toSector(result: RawResult): string | undefined {
	const section = result.section_activite_principale?.toUpperCase();
	if (section && NAF_SECTIONS[section]) return NAF_SECTIONS[section];
	return result.siege?.activite_principale ?? undefined;
}

/**
 * Search French companies by name (or SIREN/SIRET).
 * Returns up to {@link PER_PAGE} results. Network/HTTP errors are thrown so the
 * caller can decide how to surface them.
 */
export async function searchCompanies(
	query: string,
	signal?: AbortSignal,
): Promise<CompanySearchResult[]> {
	const trimmed = query.trim();
	if (!trimmed) return [];

	const url = `${SEARCH_URL}?q=${encodeURIComponent(trimmed)}&per_page=${PER_PAGE}&page=1`;
	const response = await fetch(url, { signal });
	if (!response.ok) {
		throw new Error(`recherche-entreprises responded ${response.status}`);
	}

	const data: RawResponse = await response.json();
	return (data.results ?? [])
		.map((result): CompanySearchResult | null => {
			const name = (result.nom_raison_sociale || result.nom_complet || '').trim();
			if (!name) return null;
			return {
				siren: result.siren,
				name,
				postalCode: result.siege?.code_postal ?? undefined,
				city: result.siege?.libelle_commune ?? undefined,
				sector: toSector(result),
			};
		})
		.filter((r): r is CompanySearchResult => r !== null);
}
